import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { idleDriverSummary, idleMoney, idlePayNow, idleText, idleBillingClosed, type SessionIdleView } from '@evi/shared';
import { renderInline } from '@evi/ui';
import type { SessionData } from '@/lib/api';
import styles from './IdlePanel.module.css';

export const idleClockLabel = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/** Clock ticks interpolate a server snapshot, not an amount. Losing polling
 * freezes interpolation after ten seconds; the server's confirmed fee never
 * grows from this clock, a heartbeat, visibility changes or a remount. */
export function useIdleClock(view?: SessionIdleView | null) {
  const anchor = useMemo(() => ({ at: performance.now(), server: Date.parse(view?.server_now ?? '') }), [view?.server_now]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!view?.clock_running) return;
    const timer = window.setInterval(() => setTick(n => n + 1), 1000);
    return () => window.clearInterval(timer);
  }, [view?.clock_running]);
  void tick;
  const drift = Math.max(0, performance.now() - anchor.at);
  const now = Number.isFinite(anchor.server) ? anchor.server + Math.min(drift, 10_000) : 0;
  const graceEnd = Date.parse(view?.grace_ends_at ?? '');
  const left = Number.isFinite(graceEnd) ? Math.max(0, Math.ceil((graceEnd - now) / 1000)) : 0;
  const clock = view?.clock_running ? now : Date.parse(view?.clock_until ?? '');
  const capSeconds = view && view.rate_cents > 0 ? Math.ceil(view.max_charge_cents / view.rate_cents) * 60 : 0;
  const elapsed = Number.isFinite(clock) && Number.isFinite(graceEnd)
    ? Math.min(capSeconds, Math.max(0, Math.floor((clock - graceEnd) / 1000))) : 0;
  return { left, elapsed, stale: drift > 10_000 && !!view?.clock_running };
}

export function IdlePanel({ session, onNavigate }: { session?: SessionData | null; onNavigate?: (path: string) => void }) {
  const { i18n } = useTranslation();
  const v = session?.idle_view;
  const clock = useIdleClock(v);
  if (!session || !v || (!session.ended_at && !v.charging_ended_at)) return null;
  const locale = i18n.language;
  const money = (c: number) => idleMoney(c, session.cost.currency, locale);
  const closed = !v.cable_connected && !!v.cable_removed_at;
  const headline = closed ? idleText(locale, 'unplugged')
    : v.billing_closed ? idleBillingClosed(locale)
    : v.state === 'evidence_lost' ? idleText(locale, 'evidenceLost')
    : !v.charging_ended_at ? idleText(locale, 'waiting')
    : clock.left > 0 ? idleText(locale, 'grace', { time: idleClockLabel(clock.left) })
    : idleText(locale, 'billing', { time: idleClockLabel(clock.elapsed), amount: money(v.idle_cents) });
  return <section className={styles.panel} aria-label={idleText(locale, 'idleHeading')} data-idle-state={closed ? 'unplugged' : v.state}>
    <p className={styles.eyebrow}>{idleText(locale, closed ? 'idleFinal' : 'idleHeading')}</p>
    <h2 className={styles.headline}>{renderInline(headline)}</h2>
    <p className={styles.disclosure}>{renderInline(idleDriverSummary({ time_rate: v.rate_cents,
      idle_grace_minutes: v.grace_minutes, idle_max_charge_cents: v.host_maximum ? v.max_charge_cents : null }, session.cost.currency, locale))}</p>
    <div className={styles.limits}>
      <p>{renderInline(idleText(locale, 'rate', { rate: money(v.rate_cents) }))}</p>
      <p>{renderInline(idleText(locale, 'maximum', { max: money(v.max_charge_cents) }))}</p>
      <p>{renderInline(idleText(locale, 'remaining', { amount: money(v.remaining_cap_cents) }))}</p>
    </div>
    {v.state === 'capped' && !closed && <p role="status">{idleText(locale, 'capped')}</p>}
    {!closed && !v.billing_closed && v.cable_connected && v.state !== 'evidence_lost' && v.state !== 'capped'
      && <p>{idleText(locale, 'chargeWhenUnplug')}</p>}
    {v.evidence_until && <p className={styles.evidence}>{renderInline(idleText(locale, 'confirmedAt', {
      time: new Date(v.evidence_until).toLocaleTimeString(locale) }))}</p>}
    {clock.stale && <p role="status">{idleText(locale, 'waiting')}</p>}
    {(closed || v.state === 'capped' || v.state === 'evidence_lost') && <div className={styles.final}>
      <p><span>{idleText(locale, 'idleFinal')}</span><strong>{money(v.idle_cents)}</strong></p>
      <p><span>{idleText(locale, 'total')}</span><strong>{money(v.total_cents)}</strong></p>
      {v.captured_from_hold_cents != null && <p>{renderInline(idleText(locale, 'hold', { amount: money(v.captured_from_hold_cents) }))}</p>}
      {v.follow_on_cents != null && <p>{renderInline(idleText(locale, 'followOn', { amount: money(v.follow_on_cents) }))}</p>}
      {v.follow_on_cents != null && v.follow_on_cents > 0 && <p role="status">{idleText(locale,
        v.follow_on_status === 'paid' ? 'followPaid' : v.follow_on_status === 'failed' ? 'followFailed' : 'followPending')}</p>}
      {v.settlement_pending && <p role="status">{idleText(locale, 'settlementPending')}</p>}
      {onNavigate && v.follow_on_status !== 'paid' && (v.follow_on_cents ?? 0) > 0
        && <button type="button" className={styles.pay} onClick={() => onNavigate('/billing/payment-methods')}>{idlePayNow(locale)}</button>}
    </div>}
  </section>;
}
